"use client";

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  OPEN_BOOKING_MODAL_EVENT,
  OPEN_DEMO_QUERY_PARAM,
  OPEN_DEMO_QUERY_VALUE,
} from "@/lib/constants";
import { replaceBrowserUrlPreservingHistoryState } from "@/lib/browser-history";
import { RequestDemoForm } from "@/components/request-demo-form";
import { trackEvent } from "@/lib/analytics";
import { EVENT } from "@/lib/analytics-events";

// Central tracking point: every Request-a-demo button on every page dispatches
// OPEN_BOOKING_MODAL_EVENT. Tracking here guarantees no trigger site can drift
// out of coverage. The `source` is read from event detail so we still know
// which button opened the modal.
type OpenModalDetail = { source?: string };

/**
 * Global request-demo modal for the Lumina landing page.
 * Always mounted so that the hero (and any CTA above the fold) can open the modal
 * via OPEN_BOOKING_MODAL_EVENT even before ContactSection (deferred) is in the DOM.
 */
function LandingRequestDemoModalInner() {
  const searchParams = useSearchParams();
  const prefersReducedMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Open when landing with ?openDemo=1
  useEffect(() => {
    if (searchParams.get(OPEN_DEMO_QUERY_PARAM) === OPEN_DEMO_QUERY_VALUE) {
      setIsOpen(true);
      trackEvent(EVENT.marketing_cta_clicked, {
        cta_id: "request_demo",
        source: "url_param",
      });
      const url = new URL(window.location.href);
      url.searchParams.delete(OPEN_DEMO_QUERY_PARAM);
      const cleanUrl = url.search
        ? `${url.pathname}${url.search}`
        : url.pathname;
      replaceBrowserUrlPreservingHistoryState(cleanUrl);
    }
  }, [searchParams]);

  // Listen for custom event from hero / other CTAs
  useEffect(() => {
    const handleOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenModalDetail>).detail;
      trackEvent(EVENT.marketing_cta_clicked, {
        cta_id: "request_demo",
        source: detail?.source ?? "unknown",
      });
      setIsOpen(true);
    };
    window.addEventListener(OPEN_BOOKING_MODAL_EVENT, handleOpen);
    return () =>
      window.removeEventListener(OPEN_BOOKING_MODAL_EVENT, handleOpen);
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      dialogRef.current?.focus({ preventScroll: true });
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
        return;
      }

      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          element.getClientRects().length > 0 &&
          element.getAttribute("aria-hidden") !== "true",
      );

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      if (
        document.activeElement === dialog ||
        !dialog.contains(document.activeElement)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [handleClose, isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.12 : 0.3 }}
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-[color:color-mix(in_srgb,var(--zom-rich-black)_76%,transparent)] p-3 backdrop-blur-[12px] sm:p-6"
          onClick={handleClose}
        >
          <motion.div
            ref={dialogRef}
            initial={
              prefersReducedMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.96, y: 24 }
            }
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={
              prefersReducedMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.97, y: 16 }
            }
            transition={{
              duration: prefersReducedMotion ? 0.12 : 0.36,
              ease: [0.16, 1, 0.3, 1],
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            tabIndex={-1}
            aria-modal="true"
            aria-labelledby="landing-request-demo-title"
            aria-describedby="landing-request-demo-description"
            className="relative flex max-h-[calc(100svh-1.5rem)] w-full max-w-[30rem] flex-col overflow-hidden rounded-[var(--r-card)] border border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-deep-graphite)] shadow-[var(--zom-public-dark-panel-shadow-strong)] outline-none sm:max-h-[88svh]"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 size-9 rounded-[var(--r-btn)] border border-[color:var(--zom-public-white-hairline)] bg-transparent text-[color:var(--zom-public-white-soft)] hover:border-[color:var(--zom-public-white-hairline-strong)] hover:bg-[color:var(--zom-public-white-wash)] hover:text-[color:var(--zom-public-white-strong)]"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
            <div className="shrink-0 px-5 pt-10 sm:px-10">
              <p
                id="landing-request-demo-title"
                className="font-mono text-[11px] font-normal tracking-[0.14em] text-[color:var(--zom-public-white-faint)] uppercase"
              >
                Request access
              </p>
              <p
                id="landing-request-demo-description"
                className="mt-4 font-[family-name:var(--font-body)] text-[15px] leading-[1.55] font-light text-[color:var(--zom-public-white-soft)]"
              >
                Tell us about your firm and we will set up a walkthrough on the
                systems you already run.
              </p>
            </div>
            <div className="min-h-0 overflow-y-auto overscroll-contain px-5 pt-7 pb-5 sm:px-10 sm:pb-10">
              <RequestDemoForm variant="modal" tone="dark" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * useSearchParams() opts a client component out of server rendering. When this
 * modal sat directly inside a page-level <Suspense fallback={null}>, that
 * bailout applied to the whole boundary, so the homepage, About and Security
 * pages server-rendered as an empty document and every word of their content
 * depended on JavaScript.
 *
 * Wrapping the hook-using body in its own Suspense boundary keeps the bailout
 * local to the modal: the surrounding page renders on the server as normal, and
 * only this (visually empty until opened) component defers to the client.
 */
export function LandingRequestDemoModal() {
  return (
    <Suspense fallback={null}>
      <LandingRequestDemoModalInner />
    </Suspense>
  );
}
